import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Typography, Button, Table, useToast } from '@crisellevaldez/speculo-ui';
import { Search, CheckCircle } from 'lucide-react';
import { ExpandableTagsCell } from '../../common/ExpandableTagsCell';
import { SearchableSelect } from '../../common/SearchableSelect';
import { formatPhoneNumber } from '../../../utils/formatters';
import { useUniqueContactTags } from '../../../hooks/contacts/useUniqueContactTags';
import { useContactsByTags, PAGE_SIZE, Contact } from '../../../hooks/contacts/useContactsByTags';
import { OffsetPagination } from '../../common/OffsetPagination';
import { TableCard } from '../../common/TableCard';
import { useFlowContacts } from '../../../hooks/flows/useFlowContacts';
import { ScheduleFlowModal } from './ScheduleFlowModal';
import { useUser } from '../../../hooks/useUser';
import { AdminLockOverlay } from '../client/AdminLockOverlay';

interface AddContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  flowName: string;
  flowId: string;
  // Additional flow data
  schedulingWindow?: number;
  scheduleOverUnit?: string;
  allowedDays?: string[];
  allowedHours?: {
    start_hour: number;
    end_hour: number;
  };
  excludeHolidays?: boolean;
  transferNumbers?: Record<string, string>;
  timezone?: string;
  scheduledFor?: string;
  description?: string;
  subType?: string;
}

export const AddContactsModal: React.FC<AddContactsModalProps> = ({
  isOpen,
  onClose,
  flowName,
  flowId,
  schedulingWindow,
  scheduleOverUnit,
  allowedDays,
  allowedHours,
  excludeHolidays,
  transferNumbers,
  scheduledFor,
  description,
  subType,
}) => {
  const { isAdmin } = useUser();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [addedContactsCount, setAddedContactsCount] = useState(0);
  const {
    data: tags,
    isLoading: tagsLoading,
    hasMore: tagsHasMore,
    loadMore: tagsLoadMore,
    setSearch: tagsSetSearch,
  } = useUniqueContactTags(true);
  const {
    contacts,
    totalCount,
    isLoading: contactsLoading,
    isFetching,
  } = useContactsByTags(selectedTags, flowId, shouldFetch, currentPage, PAGE_SIZE);

  const { addContactsToFlow, isAdding } = useFlowContacts();
  const { showToast } = useToast();
  const isLoading = tagsLoading || contactsLoading || isAdding;

  const handleTagsChange = (value: string[]) => {
    setSelectedTags(value);
    setShouldFetch(false);
    setCurrentPage(0);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    setShouldFetch(true);
  };

  const handleAddToFlow = async () => {
    if (!selectedTags.length) return;

    try {
      const result = await addContactsToFlow(flowId, selectedTags);

      const created = result.created ?? 0;
      const skipped = result.skipped_duplicate ?? 0;
      const errors = result.errors ?? 0;

      if (errors > 0) {
        throw new Error(`Failed to add ${errors} contact(s) to flow`);
      }

      let message = '';
      if (created > 0 && skipped === 0) {
        message = `Successfully added ${created} contact(s) to flow`;
      } else if (created > 0 && skipped > 0) {
        message = `Added ${created} contact(s) — ${skipped} already in flow`;
      } else if (skipped > 0) {
        message = `Contact(s) already in flow — no new contacts added`;
      } else {
        message = 'No matching contacts found for the selected tags';
      }

      setAddedContactsCount(created);
      showToast({ message, variant: created > 0 ? 'success' : 'info' });

      // Only show schedule modal when contacts were actually added
      if (created > 0) {
        setShowScheduleModal(true);
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to add contacts to flow',
        variant: 'error',
      });
    }
  };

  // Keep track of modal state for proper cleanup
  useEffect(() => {
    if (!isOpen) {
      setShowScheduleModal(false);
    }
  }, [isOpen]);

  const handleScheduleModalClose = useCallback(() => {
    setShowScheduleModal(false);
    // Reset states after schedule modal is closed
    setSelectedTags([]);
    setShouldFetch(false);
    setCurrentPage(0);
    onClose();
  }, [onClose]);

  const columns = [
    {
      key: 'name',
      header: 'Full Name',
      cell: (item: Contact) => `${item.firstName || ''} ${item.lastName || ''}`.trim() || '-',
    },
    {
      key: 'phoneNumber',
      header: 'Phone Number',
      cell: (item: Contact) => formatPhoneNumber(item.phoneNumber),
    },
    {
      key: 'tags',
      header: 'Tags',
      cell: (item: Contact) => {
        const tags = item.tags;
        if (!tags || !Array.isArray(tags) || tags.length === 0)
          return <span className="text-muted-foreground">-</span>;
        return <ExpandableTagsCell tags={tags} />;
      },
    },
    {
      key: 'call_tester',
      header: 'Call Tester',
      cell: (item: Contact) => (
        <div className="flex items-center">
          {item.call_tester ? (
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
          ) : (
            '-'
          )}
        </div>
      ),
    },
    {
      key: 'opt_in',
      header: 'Opt In',
      cell: (item: Contact) => (
        <div className="flex items-center">
          {item.opt_in ? (
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
          ) : (
            '-'
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal isOpen={isOpen && !showScheduleModal} onClose={onClose} size="2xl">
        <Modal.Header>
          <div className="flex flex-col gap-0.5">
            <Typography variant="h3">Add Contacts</Typography>
            <Typography variant="small" className="text-muted-foreground">
              Flow: {flowName}
            </Typography>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div data-tour="add-contacts-modal">
            <div className="space-y-5">
              {!isAdmin && (
                <AdminLockOverlay locked subject="Flow contacts" disableFields={false}>
                  <></>
                </AdminLockOverlay>
              )}
              <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                <div className="flex-1 min-w-0">
                  <SearchableSelect
                    placeholder="Select tags"
                    options={tags?.map((tag) => ({ label: tag, value: tag })) || []}
                    value={selectedTags}
                    onChange={handleTagsChange}
                    isLoading={isLoading || isFetching}
                    multiple
                    hasMore={tagsHasMore}
                    onLoadMore={tagsLoadMore}
                    onSearch={tagsSetSearch}
                    searchPlaceholder="Search tags..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleSearch}
                    disabled={isLoading || selectedTags.length === 0}
                    className="flex items-center gap-2 whitespace-nowrap h-[38px] flex-1 sm:flex-none justify-center"
                  >
                    <Search className="h-4 w-4" />
                    Search
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAddToFlow}
                    disabled={isLoading || !selectedTags.length || !isAdmin}
                    className="whitespace-nowrap h-[38px] flex-1 sm:flex-none justify-center"
                    isLoading={isAdding}
                    title={
                      !isAdmin
                        ? 'Only your organization admin can add contacts to flows'
                        : undefined
                    }
                  >
                    Add to Flow
                  </Button>
                </div>
              </div>

              {contacts.length > 0 ? (
                <TableCard
                  className="h-[calc(100dvh-200px)] sm:h-[calc(100dvh-220px)] md:h-[calc(100dvh-250px)] 3xl:h-[calc(700px)]"
                  footer={
                    totalCount > 0 ? (
                      <OffsetPagination
                        currentPage={currentPage}
                        totalItems={totalCount}
                        pageSize={PAGE_SIZE}
                        onNextPage={() => setCurrentPage((p) => p + 1)}
                        onPrevPage={() => setCurrentPage((p) => Math.max(0, p - 1))}
                        onPageChange={setCurrentPage}
                        isLoading={isLoading || isFetching}
                        label="contacts"
                      />
                    ) : undefined
                  }
                >
                  <Table
                    columns={columns}
                    data={contacts}
                    keyExtractor={(item: Contact) => item.id}
                    loading={isLoading || isFetching}
                  />
                </TableCard>
              ) : shouldFetch ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No Contacts found.
                </div>
              ) : null}
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {showScheduleModal && (
        <ScheduleFlowModal
          isOpen={true}
          onClose={handleScheduleModalClose}
          flowId={flowId}
          flowName={flowName}
          contactCount={addedContactsCount}
          selectedTags={selectedTags}
          flow={{
            id: flowId,
            name: flowName,
            scheduling_window: schedulingWindow,
            schedule_over_unit: scheduleOverUnit,
            allowed_days: allowedDays || [],
            allowed_hours: allowedHours || { start_hour: 9, end_hour: 18 },
            exclude_holidays: excludeHolidays || false,
            transfer_numbers: transferNumbers || {},
            scheduled_for: scheduledFor,
            description: description,
            sub_type: subType,
            flow_type: subType ? 'Engage' : 'Revive',
          }}
        />
      )}
    </>
  );
};
